import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import MDUserProfileHeader from './MDUserProfileHeader';
import "../Styles/UserManagement.css";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const MDYubiKeyDashboard = () => {
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { mdId } = useParams();
    const [siId] = useState(() => localStorage.getItem("siId"));

    useEffect(() => {
        if (!mdId) navigate("/", { replace: true });
    }, [mdId, navigate]);

    useEffect(() => {
        if (mdId) localStorage.setItem("md_ref", mdId);
    }, [mdId]);

    useEffect(() => {
        // 1) Must have mdId in URL
        if (!mdId) {
            navigate("/", { replace: true });
            return;
        }

        const email = localStorage.getItem("userEmail") || "";
        const isCustomer = localStorage.getItem("@customer") === "true";

        // 2) Invalid / expired MD session → clean up & go to MD login
        if (!isCustomer || !email) {
            localStorage.removeItem("@customer");
            localStorage.removeItem("userEmail");
            navigate(
                `/${mdId}/login?next=${encodeURIComponent(`/${mdId}/dashboard`)}`,
                { replace: true }
            );
            return;
        }

        // 3) Your existing logic: store IP/location, fetch role, etc.
        Axios.get("https://ipapi.co/json/")
            .then((response) => {
                const ipAddress = response.data.ip;
                const ipLocation = {
                    city: response.data.city,
                    region: response.data.region,
                    country: response.data.country_name,
                    latitude: response.data.latitude,
                    longitude: response.data.longitude,
                };

                return Axios.post("/store_user_ip_location", {
                    email,
                    ipAddress,
                    ipLocation: JSON.stringify(ipLocation),
                });
            })
            .catch(() => {
                // IP/location failure should not break dashboard
            });

        const fetchRole = () => {
            Axios.post(`/get_user_role`, { email })
                .then((res) => {
                    if (res.data?.success) {
                        setIsAdmin(!!res.data.is_admin);
                    } else {
                        toast.error("Failed to retrieve user role");
                    }
                })
                .catch(() => {
                    toast.error("An error occurred while fetching user role.");
                });
        };

        fetchRole();
    }, [mdId, navigate]);

    const keysStatusMap = {
        1: 'Pending Assignment',
        2: 'Pending Activation',
        3: 'Activated',
        4: 'Lost',
        5: 'Damaged',
        6: 'Deleted',
    };

    const formatFetchedKeys = (keys) => {
        return keys.map(key => ({
            ...key,
            status: keysStatusMap[key.status] || key.status
        }));
    }

    const fetchKeys = () => {
        Axios.post(`/get_customer_keys`, {mdId, siId}).then((response) => {
            if (response.data.success) {
                setUsers(formatFetchedKeys(response.data.data));
            } else {
                toast.error("Failed to get all users.", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        });
    };

    useEffect(() => {
        fetchKeys();
    }, []);

    const handleSelectUser = (userEmail) => {
        setSelectedUsers((prev) => {
            if (prev.includes(userEmail)) {
                return prev.filter((email) => email !== userEmail);
            } else {
                return [...prev, userEmail];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(user => user.email));
        }
        setSelectAll(!selectAll);
    };

    const handleDeleteUsers = () => {
        Axios.post(`/delete_velox_cloud`, { emails: selectedUsers }).then((response) => {
            if (response.data.success) {
                toast.success(`${response.data.msg}`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
            fetchKeys();
            setSelectedUsers([]);
            setOpenDialog(false);;
        });
    };

    const handleBack = () => {
        console.log("Back button clicked!");
        console.log("Current path:", location.pathname);
        // Dismiss any active toasts before navigating
        toast.dismiss();
        // Check if we can go back in history
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);  // Go back one step in history
            console.log("Navigating back one step.");
        } else {
            // If no history, navigate to the dashboard without adding an entry
            navigate(`/${mdId}/dashboard`, { replace: true });
            console.log("No history to go back, redirecting to dashboard.");
        }
    };

    return (
        <div className="dashboard">
            <MDUserProfileHeader isAdmin={true} />

            <div className="all-users-card">
                <h1 className="Title-alluser">VELOX Cloud Key Dashboard</h1>

                <div className="button-row">
                    <button onClick={fetchKeys} className="refresh-button">Refresh</button>
                    <button onClick={handleBack} className="save-button">Save</button>
                    <button
                        onClick={() => setOpenDialog(true)}
                        className="delete-button"
                        disabled={selectedUsers.length === 0}
                    >
                        Delete
                    </button>
                </div>

                <div className="table-container">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>Order</th>
                                <th>VELOX Cloud Key Serial Number</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Company Name</th>
                                <th>MD ID</th>
                                <th>SI ID</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((yk, index) => (
                                <tr key={yk.serial_number}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(yk.serial_number)}
                                            onChange={() => handleSelectUser(yk.serial_number)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{yk.serial_number}</td>
                                    <td>{yk.first_name}</td>
                                    <td>{yk.last_name}</td>
                                    <td>{yk.email}</td>
                                    <td>{yk.company_name}</td>
                                    <td>{yk.md_id}</td>
                                    <td>{yk.si_id}</td>
                                    <td>{yk.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete Users"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete these users?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteUsers} color="primary">Yes</Button>
                        <Button onClick={() => setOpenDialog(false)} color="secondary" autoFocus>No</Button>
                    </DialogActions>
                </Dialog>
            </div>

            <ToastContainer />
        </div>
    );

};

export default MDYubiKeyDashboard;
